import {
  Avatar,
  Box,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import React, { useMemo, useState } from 'react'
import { Button, IconButton, InformationModal, SkeletonText } from 'utoniq-ui'

import { BaseLayout, Head } from '@/components/layouts'
import { useMessage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { generateFullPath } from '@/lib/url'

export * from './fragments'

import { useCopyToClipboard } from 'usehooks-ts'

import {
  Maybe,
  TicketShareCopyUserItemSerialFragment,
} from '@/clients/utoniq-core/schema'
import { getSerialFormatNumber } from '@/components/organisms'
import { ArtistThemeProvider } from '@/components/styles'

export type State = {
  loading: boolean
  canceling: boolean
  isFirstGenerate: boolean
  regenerated: boolean
  regenerating: boolean
  artistId: string
  throughTicketIndex?: string
  serial: Maybe<TicketShareCopyUserItemSerialFragment>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onRegenerateTicketDistribution: () => Promise<void>
  onCancelTicketDistribution: () => Promise<void>
}

export const TicketShareCopyTemplate: Template<State, Action> = ({
  state,
  onRegenerateTicketDistribution,
  onCancelTicketDistribution,
}) => {
  const { t } = useTranslation('ticketShareCopy')
  const { routerNames, router } = useRouter()

  const ticketNumber = useMemo(() => {
    return getSerialFormatNumber(state.serial?.serial, state.throughTicketIndex)
  }, [state.serial?.serial, state.throughTicketIndex])

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <BaseLayout
        header={{
          title: t('title'),
          right: (
            <IconButton.Close edge="end" onClick={() => router.goBack()} />
          ),
        }}
      >
        <Head title={routerNames.TicketShareCopy} />
        {state.loading ? (
          <TicketShareCopySkeleton />
        ) : (
          <TicketShareCopyContentCell
            regenerating={state.regenerating}
            regenerated={state.regenerated}
            isFirstGenerate={state.isFirstGenerate}
            canceling={state.canceling}
            serial={state.serial}
            ticketNumber={ticketNumber}
            onCancelTicketDistribution={onCancelTicketDistribution}
            onRegenerateTicketDistribution={onRegenerateTicketDistribution}
          />
        )}
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

type TicketShareCopyContentCellProps = TicketShareCopyCellProps &
  TicketShareCopyCancelCellProps &
  TicketShareCopyUserCancelCellProps

const TicketShareCopyContentCell: Cell<TicketShareCopyContentCellProps> =
  props => {
    if (!props.serial?.serial?.ticketDistributionLink) {
      return <TicketShareCopyCancelCell {...props} />
    }
    if (props.serial?.serial?.ticketDistributionLink?.user) {
      return <TicketShareCopyUserCancelCell {...props} />
    }
    return <TicketShareCopyCell {...props} />
  }

type TicketShareCopyCellProps = {
  canceling: boolean
  regenerating: boolean
  regenerated: boolean
  isFirstGenerate: boolean
  serial: State['serial']
  ticketNumber: string
  onRegenerateTicketDistribution: Action['onRegenerateTicketDistribution']
  onCancelTicketDistribution: Action['onCancelTicketDistribution']
}
const TicketShareCopyCell: Cell<TicketShareCopyCellProps> = ({
  canceling,
  regenerating,
  regenerated,
  isFirstGenerate,
  serial,
  ticketNumber,
  onRegenerateTicketDistribution,
  onCancelTicketDistribution,
}) => {
  const { t } = useTranslation('ticketShareCopy')
  const [_, copy] = useCopyToClipboard()
  const { showMessage } = useMessage()
  const { routerPages } = useRouter()

  const [openReissue, setOpenReissue] = useState(false)
  const [openCancelConfirm, setOpenCancelConfirm] = useState(false)

  const link = useMemo(() => {
    const token = serial?.serial?.ticketDistributionLink?.token
    if (!token) {
      return ''
    }
    return generateFullPath(routerPages.TicketShareConfirm, { token })
  }, [
    routerPages.TicketShareConfirm,
    serial?.serial?.ticketDistributionLink?.token,
  ])

  const formattedExpiredAt = useMemo(
    () => serial?.serial?.ticketDistributionLink?.formattedExpiredAt,
    [serial?.serial?.ticketDistributionLink?.formattedExpiredAt]
  )

  const handleCopy = async () => {
    await copy(t('note', { link, expired: formattedExpiredAt }))
    showMessage(t('copyURLSuccess'))
  }
  const handleReissueUrl = () => {
    onRegenerateTicketDistribution().finally(() => {
      setOpenReissue(false)
    })
  }

  const handleCancelDistribution = () => {
    onCancelTicketDistribution().finally(() => {
      setOpenCancelConfirm(false)
    })
  }

  return (
    <CellFrame sx={{ px: 5, py: 4 }}>
      <Stack spacing={2} pb={2}>
        <Typography variant="overline" color="text.secondary">
          {t('distribute', '分配中')}
        </Typography>
        <Typography variant="subtitle1">{ticketNumber}</Typography>
      </Stack>
      <Box sx={{ py: 2 }}>
        <Typography variant="body2">
          {t(
            regenerated
              ? 'descriptionRegenerated'
              : isFirstGenerate
              ? 'descriptionFirstGenerate'
              : 'description'
          )}
        </Typography>
      </Box>
      <Card elevation={0} sx={{ borderRadius: 2, mt: 2 }}>
        <CardContent sx={{ p: 4, '&:last-child': { pb: 4 } }}>
          <Typography variant="caption" component="p">
            {t('note', { link, expired: formattedExpiredAt })}
          </Typography>
        </CardContent>
      </Card>
      <Stack sx={{ mt: 3 }}>
        <Typography variant="caption">{t('notes')}</Typography>
      </Stack>

      <Stack sx={{ p: 4 }} spacing={3} alignItems="center">
        <Button size="large" sx={{ width: 244 }} onClick={handleCopy}>
          {t('makeCopy')}
        </Button>
        {!isFirstGenerate && (
          <>
            <Button
              size="large"
              variant="outlined"
              sx={{ width: 244 }}
              onClick={() => setOpenReissue(true)}
            >
              {t('reissue')}
            </Button>
            <Button
              size="large"
              variant="text"
              sx={{ width: 244 }}
              onClick={() => setOpenCancelConfirm(true)}
            >
              {t('cancelDistribution')}
            </Button>
          </>
        )}
      </Stack>
      <InformationModal
        open={openReissue}
        onClose={() => setOpenReissue(false)}
        title={
          <Typography variant="h6" sx={{ pb: 3 }}>
            {t('reissueTitle')}
          </Typography>
        }
        content=""
        cancelText={t('close')}
        buttons={
          <Button
            loading={regenerating}
            size="large"
            fullWidth
            onClick={handleReissueUrl}
          >
            {t('OK')}
          </Button>
        }
      />
      <InformationModal
        open={openCancelConfirm}
        onClose={() => setOpenCancelConfirm(false)}
        title={
          <Typography variant="h6" sx={{ pb: 3 }}>
            {t('cancelConfirmTitle')}
          </Typography>
        }
        content=""
        cancelText={t('close')}
        buttons={
          <Button
            loading={canceling}
            size="large"
            fullWidth
            onClick={handleCancelDistribution}
          >
            {t('OK')}
          </Button>
        }
      />
    </CellFrame>
  )
}

const TicketShareCopySkeleton: Cell = () => (
  <CellFrame sx={{ p: 5 }}>
    <Box sx={{ py: 2 }}>
      <Stack spacing={1}>
        <SkeletonText variant="body2" width="100%" />
        <SkeletonText variant="body2" width="100%" />
        <SkeletonText variant="body2" width="50%" />
      </Stack>
    </Box>
    <Card elevation={0} sx={{ borderRadius: 2, mt: 5 }}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={6}>
          <Stack spacing={3}>
            <SkeletonText variant="body2" width="45%" />
            <SkeletonText variant="body2" width="100%" />
          </Stack>
          <Stack spacing={3}>
            <SkeletonText variant="body2" width="45%" />
            <SkeletonText variant="body2" width="100%" />
            <SkeletonText variant="body2" width="100%" />
            <SkeletonText variant="body2" width="100%" />
          </Stack>
          <Stack spacing={3}>
            <SkeletonText variant="body2" width="45%" />
            <SkeletonText variant="body2" width="100%" />
          </Stack>
        </Stack>
      </CardContent>
    </Card>
    <SkeletonText variant="body2" width="50%" />
    <SkeletonText variant="body2" width="45%" sx={{ mt: 4 }} />
    <SkeletonText variant="body2" width="100%" sx={{ mt: 2 }} />

    <Stack sx={{ py: 6, width: '100%' }} alignItems="center">
      <Skeleton
        variant="rectangular"
        height="44px"
        width="50%"
        sx={{ borderRadius: '34px' }}
      />
    </Stack>
  </CellFrame>
)

type TicketShareCopyCancelCellProps = {
  ticketNumber: string
}
const TicketShareCopyCancelCell: Cell<TicketShareCopyCancelCellProps> = ({
  ticketNumber,
}) => {
  const { t } = useTranslation('ticketShareCopy')
  const { router, generatePath, routerPages } =
    useRouter<{ itemId: string; serialId: string }>()

  return (
    <CellFrame sx={{ px: 5, py: 4 }}>
      <Stack spacing={2}>
        <Typography variant="overline" color="text.secondary">
          {t('cancelDistribute', '分配キャンセル')}
        </Typography>
        <Typography variant="subtitle1">{ticketNumber}</Typography>
      </Stack>
      <Stack sx={{ p: 4 }} spacing={3} alignItems="center">
        <Button
          size="large"
          variant="text"
          sx={{ width: 244 }}
          onClick={() =>
            router.replace(
              generatePath(routerPages.TicketShareSelect, {
                itemId: router.query.itemId,
                serialId: router.query.serialId,
              })
            )
          }
        >
          {t('redoDistribute', '分配をやり直す')}
        </Button>
      </Stack>
    </CellFrame>
  )
}

type TicketShareCopyUserCancelCellProps = {
  serial: State['serial']
  canceling: State['canceling']
  ticketNumber: string
  onCancelTicketDistribution: Action['onCancelTicketDistribution']
}
const TicketShareCopyUserCancelCell: Cell<TicketShareCopyUserCancelCellProps> =
  ({ serial, canceling, ticketNumber, onCancelTicketDistribution }) => {
    const { t } = useTranslation('ticketShareCopy')
    const [openCancelConfirm, setOpenCancelConfirm] = useState(false)
    const user = serial?.serial?.ticketDistributionLink?.user

    const handleCancelDistribution = () => {
      onCancelTicketDistribution().finally(() => {
        setOpenCancelConfirm(false)
      })
    }

    return (
      <CellFrame sx={{ px: 5, py: 4 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar
              src={user?.avatar?.smallUri ?? ''}
              sx={{ width: 16, height: 16 }}
            />
            <Typography variant="overline" color="text.secondary">
              {t('distributedToUser', { name: user?.displayName })}
            </Typography>
          </Stack>
          <Typography variant="subtitle1">{ticketNumber}</Typography>
        </Stack>
        <Stack sx={{ p: 4 }} spacing={3} alignItems="center">
          <Button
            size="large"
            variant="text"
            sx={{ width: 244 }}
            onClick={() => setOpenCancelConfirm(true)}
          >
            {t('cancelDistribution', '分配をキャンセルする')}
          </Button>
        </Stack>
        <InformationModal
          open={openCancelConfirm}
          onClose={() => setOpenCancelConfirm(false)}
          title={
            <Typography variant="h6" sx={{ pb: 3 }}>
              {t('cancelConfirmTitle')}
            </Typography>
          }
          content=""
          cancelText={t('close')}
          buttons={
            <Button
              loading={canceling}
              size="large"
              fullWidth
              onClick={handleCancelDistribution}
            >
              {t('OK')}
            </Button>
          }
        />
      </CellFrame>
    )
  }
